import { useState } from 'react';

import { ModalBase } from '../../supervisor/components/modals/ModalBase';
import { useDelegateSteps } from '../hooks/use-delegate-steps';

import type { ConversationDetail } from '../../../lib/types';
import type React from 'react';

export interface DelegateStepsModalProps {
  conversation: ConversationDetail;
  open: boolean;
  onClose: () => void;
}

/**
 * Timeout de SEGURANÇA — presets (§4.1). O endpoint SEMPRE grava um
 * `timeoutAt` (Q-D10, `step-delegation.util.ts`
 * `DELEGATION_DEFAULT_TIMEOUT_MIN=120`) — não existe "sem timeout" real,
 * só o padrão generoso de 2h quando `timeoutMinutes` é omitido. O rótulo
 * "Padrão de segurança" (em vez de "sem limite", que seria falso) reflete
 * isso honestamente.
 */
const TIMEOUT_PRESETS: { id: string; label: string; minutes: number | undefined }[] = [
  { id: '15', label: '15 min', minutes: 15 },
  { id: '30', label: '30 min', minutes: 30 },
  { id: '60', label: '60 min', minutes: 60 },
  { id: 'default', label: 'Padrão de segurança (2h)', minutes: undefined },
];

/**
 * DelegateStepsModal — S7 PR1 (§4.1/§4.3, Decisão 2).
 *
 * Reconciliação mockup Briefing 2A × endpoint real `POST
 * .../delegate-steps` (S5, ADR-154): o endpoint só suporta
 * `{objectives?: string[], timeoutMinutes?}` — o radio "tipo de janela"
 * (uma resposta/até gatilho/indeterminado) e os checkboxes de gatilho do
 * mockup NÃO têm respaldo e ficam de fora do v1 (deferido, spec §8).
 *
 * Objetivos vêm dos `steps` do playbook ATIVO da conversa
 * (`conversation.playbook.steps[].objective` — PERGUNTA-4: o wire JÁ expõe
 * os steps no detail; `objective` foi adicionado nesta PR ao lado de
 * `id`/`name` porque é o valor que o backend reconhece, não `id`/`name`).
 * Nenhum objetivo selecionado ⇒ escopo = todas as etapas completáveis
 * (comportamento documentado do próprio endpoint — omite `objectives`).
 *
 * **Fix pós-revisão adversarial (BLOCKER Reviewer A #1):** só entram como
 * SELECIONÁVEIS os steps com `completable === true` — campo derivado no
 * Engine (`conversations.serialization.ts`) via `isCompletableStep`, a
 * MESMA função pura que `evaluateStepCompletion` usa para fechar a janela.
 * Um step com `objective` mas SEM `successSignal`/`requiredFields`
 * (`completable=false`) jamais fecha deterministicamente — oferecê-lo como
 * "volta quando os objetivos fecharem" seria uma promessa estruturalmente
 * falsa (a janela só devolveria no timeout de segurança). Quando NENHUM
 * step é completável, a copy do topo E a nota da seção "Objetivos" viram
 * honestas (só timeout de segurança) — a promessa é sempre CONDICIONAL à
 * existência de objetivos completáveis, nunca fixa.
 *
 * **Gatear o fetch/mutation, não só o render** (lição B2 — molde
 * `PanelSheet`/`BookingSheet` mobile): fechado ⇒ `DelegateStepsModalContent`
 * (dona do `useDelegateSteps`/`useMutation`) NEM MONTA — zero
 * subscription residual com o modal fechado.
 */
export function DelegateStepsModal({
  conversation,
  open,
  onClose,
}: DelegateStepsModalProps): React.JSX.Element | null {
  if (!open) return null;
  return <DelegateStepsModalContent conversation={conversation} onClose={onClose} />;
}

function DelegateStepsModalContent({
  conversation,
  onClose,
}: {
  conversation: ConversationDetail;
  onClose: () => void;
}): React.JSX.Element {
  const { mutate, isPending } = useDelegateSteps(conversation.id);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [timeoutPresetId, setTimeoutPresetId] = useState<string>('default');
  const [failed, setFailed] = useState(false);

  // Só steps COMPLETÁVEIS (successSignal ou requiredFields declarados no
  // backend — vide `completable`) são ofertados como selecionáveis (BLOCKER
  // Reviewer A #1). `completable` ausente (wire retrocompat/detail antigo)
  // é tratado como NÃO completável — fail-closed, nunca assume `true` por
  // ausência.
  const steps = (conversation.playbook?.steps ?? []).flatMap((s) =>
    s.objective !== null &&
    s.objective !== undefined &&
    s.objective.length > 0 &&
    s.completable === true
      ? [{ id: s.id, name: s.name, objective: s.objective }]
      : [],
  );

  function toggle(objective: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(objective)) {
        next.delete(objective);
      } else {
        next.add(objective);
      }
      return next;
    });
  }

  function handleClose(): void {
    setSelected(new Set());
    setTimeoutPresetId('default');
    setFailed(false);
    onClose();
  }

  async function handleSubmit(): Promise<void> {
    setFailed(false);
    const preset = TIMEOUT_PRESETS.find((p) => p.id === timeoutPresetId);
    try {
      await mutate({
        ...(selected.size > 0 && { objectives: Array.from(selected) }),
        ...(preset?.minutes !== undefined && { timeoutMinutes: preset.minutes }),
      });
      handleClose();
    } catch {
      setFailed(true);
    }
  }

  return (
    <ModalBase
      open
      onClose={handleClose}
      title="Delegar etapas à IA"
      width={520}
      footer={
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <button
            className="btn-ghost-mini"
            type="button"
            onClick={handleClose}
            disabled={isPending}
          >
            Cancelar
          </button>
          <button
            className="btn-ghost-mini btn-ghost-mini--primary"
            type="button"
            disabled={isPending}
            onClick={() => void handleSubmit()}
            data-testid="delegate-steps-submit"
          >
            {isPending ? 'Delegando…' : 'Delegar'}
          </button>
        </div>
      }
    >
      <div className="modal-form">
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-2)', marginBottom: 8 }}>
          {steps.length > 0
            ? 'A IA conduz autonomamente (H_c) os objetivos abaixo. A conversa volta a você automaticamente quando os objetivos fecharem — ou no timeout de segurança.'
            : 'Este playbook não tem etapas com fechamento automático — a IA conduz e devolve apenas no timeout de segurança.'}
        </p>

        <span
          className="modal-form__label"
          style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}
        >
          Objetivos
        </span>
        {steps.length === 0 ? (
          <p
            style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-3)', marginBottom: 12 }}
            data-testid="delegate-steps-no-steps"
          >
            Nenhuma etapa deste playbook tem fechamento automático — a IA conduz e devolve no tempo
            de segurança.
          </p>
        ) : (
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}
            role="group"
            aria-label="Objetivos a delegar"
          >
            {steps.map((step) => (
              <label className="checkbox" key={step.id}>
                <input
                  type="checkbox"
                  checked={selected.has(step.objective)}
                  onChange={() => toggle(step.objective)}
                  data-testid={`delegate-steps-objective-${step.id}`}
                />
                <span className="checkbox__box" aria-hidden="true" />
                {step.name}
              </label>
            ))}
          </div>
        )}

        <span
          className="modal-form__label"
          style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}
        >
          Devolver a mim após — segurança
        </span>
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}
          role="radiogroup"
          aria-label="Timeout de segurança"
        >
          {TIMEOUT_PRESETS.map((preset) => (
            <label className="radio" key={preset.id}>
              <input
                type="radio"
                name="delegate-steps-timeout"
                checked={timeoutPresetId === preset.id}
                onChange={() => setTimeoutPresetId(preset.id)}
                data-testid={`delegate-steps-timeout-${preset.id}`}
              />
              <span className="radio__box" aria-hidden="true" />
              {preset.label}
            </label>
          ))}
        </div>

        {failed && (
          <div className="banner banner--danger" role="alert">
            <div className="banner__body">
              <span className="banner__title">
                Não foi possível abrir a delegação. Tente novamente.
              </span>
            </div>
          </div>
        )}
      </div>
    </ModalBase>
  );
}
